import React, { useState, useRef } from 'react';
import { X, Building2, Sparkles, Upload, Trash2 } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { slugify } from '@sahaj/utils';
import { WorkspaceLogo } from '../common/WorkspaceLogo';
import { ImageCropperModal } from './ImageCropperModal';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({ isOpen, onClose }) => {
  const { createWorkspace, uploadWorkspaceIcon } = useWorkspace();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Icon Cropping State
  const [selectedCropFile, setSelectedCropFile] = useState<File | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleNameChange = (val: string) => {
    setName(val);
    setSlug(slugify(val));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, WebP, GIF, SVG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    setSelectedCropFile(file);
    setIsCropperOpen(true);
  };

  const handleCropComplete = (croppedFile: File, previewUrl: string) => {
    setIconFile(croppedFile);
    setIconPreview(previewUrl);
    setSelectedCropFile(null);
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    if (iconPreview) {
      URL.revokeObjectURL(iconPreview);
      setIconPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const created = await createWorkspace(name.trim(), slug.trim(), description.trim() || undefined);

      // If an icon file was selected during onboarding, upload it for the created workspace
      if (iconFile && created?.id) {
        try {
          await uploadWorkspaceIcon(iconFile);
        } catch (uploadErr) {
          console.warn('Workspace created, but icon upload failed:', uploadErr);
        }
      }

      setName('');
      setSlug('');
      setDescription('');
      handleRemoveIcon();
      onClose();
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to create workspace');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Building2 className="h-4 w-4" />
            </div>
            <span>Create a Workspace</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium">
              {error}
            </div>
          )}

          {/* Optional Workspace Icon Upload */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Workspace Icon <span className="text-muted-foreground font-normal">(Optional)</span>
            </label>
            <div className="flex items-center gap-3.5 p-3 rounded-xl bg-muted/40 border border-border">
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-border shadow-2xs">
                {iconPreview ? (
                  <img src={iconPreview} alt="Workspace Icon Preview" className="w-full h-full object-cover" />
                ) : (
                  <WorkspaceLogo name={name || 'Workspace'} size="lg" className="w-full h-full text-base rounded-xl" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  className="hidden"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 bg-background hover:bg-muted text-foreground border border-border rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition shadow-2xs"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    <span>{iconPreview ? 'Change Icon' : 'Upload Icon'}</span>
                  </button>

                  {iconPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveIcon}
                      className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition"
                      title="Remove Icon"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG, WebP, SVG up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Workspace Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Acme Corporation"
              className="w-full h-10 px-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">URL Slug</label>
            <div className="flex items-center">
              <span className="h-10 px-3 bg-muted border border-r-0 border-border rounded-l-xl text-xs text-muted-foreground flex items-center">
                sahajconnect.com/
              </span>
              <input
                type="text"
                required
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                placeholder="acme-corp"
                className="flex-1 h-10 px-3 bg-background border border-border rounded-r-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition font-mono text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this workspace for?"
              rows={2}
              className="w-full p-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition resize-none"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" />
              <span>{isSubmitting ? 'Creating...' : 'Create Workspace'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Interactive Workspace Logo Cropper */}
      {isCropperOpen && selectedCropFile && (
        <ImageCropperModal
          isOpen={isCropperOpen}
          imageFile={selectedCropFile}
          cropShape="rounded-square"
          title="Crop Workspace Logo"
          onClose={() => {
            setIsCropperOpen(false);
            setSelectedCropFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};
